import { useEffect, useState } from "react";
import Breadcrumb from "@/Components/Breadcrumbs/Breadcrumb";
import InputLabel from "@/Components/InputLabel";
import InputError from "@/Components/InputError";
import LogoDark from "../../images/logo/logo-dark.svg";
import Logo from "../../images/logo/logo.svg";
import { useForm, Link, usePage } from "@inertiajs/react";
import DefaultLayout from "@/Layouts/DefaultLayout";

export default function Login({
    canResetPassword,
    otpLoginEnabled = false,
    status,
}) {
    const [loginMethod, setLoginMethod] = useState("password");
    const [otpStep, setOtpStep] = useState("request");

    const passwordForm = useForm({
        email: "",
        password: "",
        remember: false,
    });

    const otpForm = useForm({
        email: "",
        code: "",
        remember: false,
    });

    const { props } = usePage();
    const { errors, globalSettings, buckets, tenantSlug } = props;

    const staticAssetsBucket = buckets["static_assets"].name;
    const profileImagesRegion = buckets["profile_images"].region;

    const logoBaseUrl = `https://${staticAssetsBucket}.s3.${profileImagesRegion}.amazonaws.com/${tenantSlug}/`;

    useEffect(() => {
        return () => {
            passwordForm.reset("password");
            otpForm.reset("code");
        };
    }, []);

    useEffect(() => {
        if (status && loginMethod === "otp") {
            setOtpStep("code");
        }
    }, [status, loginMethod]);

    const submitPassword = (e) => {
        e.preventDefault();
        passwordForm.post(route("login"));
    };

    const submitOtpSend = (e) => {
        e.preventDefault();
        otpForm.post(route("login.otp.send"), {
            preserveScroll: true,
            onSuccess: () => setOtpStep("code"),
        });
    };

    const submitOtpVerify = (e) => {
        e.preventDefault();
        otpForm.post(route("login.otp.verify"));
    };

    const showOtp = Boolean(otpLoginEnabled);

    return (
        <DefaultLayout>
            <div className="mt-5 mx-auto max-w-screen-2xl">
                <Breadcrumb
                    pageName="Welcome!"
                    pageLink="/"
                    pageLinkName="Home"
                />
                <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
                    <div className="flex flex-wrap items-center">
                        <div className="hidden w-full xl:block xl:w-1/2">
                            <div className="px-26 py-17.5 text-center">
                                <div className="mb-5.5 inline-block">
                                    <img
                                        className="hidden dark:block w-80"
                                        src={
                                            globalSettings.logo_dark_file_name ===
                                            null
                                                ? Logo
                                                : logoBaseUrl +
                                                  globalSettings.logo_dark_file_name
                                        }
                                        alt="Logo"
                                    />
                                    <div className="mb-5.5 flex items-center space-x-4">
                                        <img
                                            className="dark:hidden w-80"
                                            src={
                                                globalSettings.logo_dark_file_name ===
                                                null
                                                    ? LogoDark
                                                    : logoBaseUrl +
                                                      globalSettings.logo_file_name
                                            }
                                            alt="Logo"
                                        />
                                    </div>
                                </div>

                                <p className="font-bold text-black dark:text-white">
                                    Great to see you again!
                                </p>
                                <p className="2xl:px-20">
                                    Log in and pick up where you left off.
                                </p>
                            </div>
                        </div>
                        <div className="w-full border-stroke dark:border-strokedark xl:w-1/2 xl:border-l-2">
                            <div className="w-full p-4 sm:p-12.5 xl:p-17.5">
                                <h2 className="mb-9 text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
                                    Sign In
                                </h2>

                                {showOtp && (
                                    <div className="mb-6 flex rounded-lg border border-stroke p-1 dark:border-strokedark">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setLoginMethod("password");
                                                setOtpStep("request");
                                            }}
                                            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition ${
                                                loginMethod === "password"
                                                    ? "bg-primary text-white"
                                                    : "text-black dark:text-white"
                                            }`}
                                        >
                                            Password
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setLoginMethod("otp")
                                            }
                                            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition ${
                                                loginMethod === "otp"
                                                    ? "bg-primary text-white"
                                                    : "text-black dark:text-white"
                                            }`}
                                        >
                                            SMS code
                                        </button>
                                    </div>
                                )}

                                {status && (
                                    <p className="mb-4 text-sm text-green-600 dark:text-green-400">
                                        {status}
                                    </p>
                                )}

                                {(!showOtp || loginMethod === "password") && (
                                    <form onSubmit={submitPassword}>
                                        <div className="mb-4">
                                            <InputLabel
                                                htmlFor="email"
                                                value="Email"
                                            />
                                            <input
                                                id="email"
                                                type="email"
                                                name="email"
                                                value={passwordForm.data.email}
                                                autoComplete="username"
                                                onChange={(e) =>
                                                    passwordForm.setData(
                                                        "email",
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="Enter your email"
                                                className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                                            />
                                            <InputError
                                                message={errors.email}
                                                className="mt-2 text-red"
                                            />
                                        </div>

                                        <div className="mb-6">
                                            <InputLabel
                                                htmlFor="password"
                                                value="Password"
                                            />
                                            <input
                                                id="password"
                                                type="password"
                                                name="password"
                                                value={
                                                    passwordForm.data.password
                                                }
                                                onChange={(e) =>
                                                    passwordForm.setData(
                                                        "password",
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="6+ Characters, 1 Capital letter"
                                                className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                                            />
                                            <InputError
                                                message={errors.password}
                                                className="mt-2 text-red"
                                            />
                                        </div>

                                        <div className="mb-4 block">
                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    name="remember"
                                                    checked={
                                                        passwordForm.data
                                                            .remember
                                                    }
                                                    onChange={(e) =>
                                                        passwordForm.setData(
                                                            "remember",
                                                            e.target.checked,
                                                        )
                                                    }
                                                    className="form-checkbox"
                                                />
                                                <span className="text-gray-600 ms-2 text-sm">
                                                    Remember me
                                                </span>
                                            </label>
                                        </div>

                                        <div className="mb-5">
                                            <input
                                                type="submit"
                                                value="Sign In"
                                                className="w-full cursor-pointer rounded-lg border border-primary bg-primary p-4 text-white transition hover:bg-opacity-90"
                                                disabled={
                                                    passwordForm.processing
                                                }
                                            />
                                        </div>
                                    </form>
                                )}

                                {showOtp && loginMethod === "otp" && (
                                    <form
                                        onSubmit={
                                            otpStep === "request"
                                                ? submitOtpSend
                                                : submitOtpVerify
                                        }
                                    >
                                        <div className="mb-4">
                                            <InputLabel
                                                htmlFor="otp_email"
                                                value="Email"
                                            />
                                            <input
                                                id="otp_email"
                                                type="email"
                                                name="email"
                                                value={otpForm.data.email}
                                                autoComplete="username"
                                                onChange={(e) =>
                                                    otpForm.setData(
                                                        "email",
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="Enter your email"
                                                className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                                            />
                                            <InputError
                                                message={errors.email}
                                                className="mt-2 text-red"
                                            />
                                        </div>

                                        {otpStep === "code" && (
                                            <div className="mb-6">
                                                <InputLabel
                                                    htmlFor="code"
                                                    value="SMS code"
                                                />
                                                <input
                                                    id="code"
                                                    type="text"
                                                    name="code"
                                                    inputMode="numeric"
                                                    autoComplete="one-time-code"
                                                    value={otpForm.data.code}
                                                    onChange={(e) =>
                                                        otpForm.setData(
                                                            "code",
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="Enter the code from your phone"
                                                    className="w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:text-white dark:focus:border-primary"
                                                />
                                                <InputError
                                                    message={errors.code}
                                                    className="mt-2 text-red"
                                                />
                                            </div>
                                        )}

                                        <div className="mb-4 block">
                                            <label className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    name="remember"
                                                    checked={
                                                        otpForm.data.remember
                                                    }
                                                    onChange={(e) =>
                                                        otpForm.setData(
                                                            "remember",
                                                            e.target.checked,
                                                        )
                                                    }
                                                    className="form-checkbox"
                                                />
                                                <span className="text-gray-600 ms-2 text-sm">
                                                    Remember me
                                                </span>
                                            </label>
                                        </div>

                                        <div className="mb-5">
                                            <input
                                                type="submit"
                                                value={
                                                    otpStep === "request"
                                                        ? "Send SMS code"
                                                        : "Verify & Sign In"
                                                }
                                                className="w-full cursor-pointer rounded-lg border border-primary bg-primary p-4 text-white transition hover:bg-opacity-90"
                                                disabled={otpForm.processing}
                                            />
                                        </div>

                                        {otpStep === "code" && (
                                            <button
                                                type="button"
                                                className="mb-5 w-full text-center text-sm text-primary underline"
                                                onClick={() => {
                                                    setOtpStep("request");
                                                    otpForm.reset("code");
                                                }}
                                            >
                                                Resend or change email
                                            </button>
                                        )}
                                    </form>
                                )}

                                {canResetPassword &&
                                    (!showOtp ||
                                        loginMethod === "password") && (
                                        <div className="pb-4 text-center">
                                            <Link
                                                href={route("password.request")}
                                                className="rounded-md text-sm text-gray-600 underline hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                            >
                                                Forgot your password?
                                            </Link>
                                        </div>
                                    )}

                                <a
                                    href={route("register")}
                                    rel="noopener noreferrer"
                                    className="flex w-full items-center justify-center rounded-lg border border-stroke bg-gray p-4 hover:bg-opacity-50 dark:border-strokedark dark:bg-meta-4 dark:hover:bg-opacity-50"
                                >
                                    <img
                                        width={50}
                                        className="dark:block hidden"
                                        src={
                                            globalSettings?.logo_dark_file_name
                                                ? logoBaseUrl +
                                                  globalSettings.logo_dark_file_name
                                                : Logo
                                        }
                                        alt="Dark Logo"
                                    />
                                    <img
                                        width={50}
                                        className="dark:hidden"
                                        src={
                                            globalSettings.logo_file_name ===
                                            null
                                                ? LogoDark
                                                : logoBaseUrl +
                                                  globalSettings.logo_file_name
                                        }
                                        alt="Light Logo"
                                    />
                                    <span className="pl-1">
                                        Community Sign Up
                                    </span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </DefaultLayout>
    );
}
